import clsx from 'clsx';
import * as React from 'react';

export interface ListRenderItemInfo<Item> {
    /** The item to render */
    item: Item;
    /** The index of the item in the list */
    index: number;
}

type ListAs = 'ul' | 'ol';

type ListElement<TAs extends ListAs> = TAs extends 'ul' ? HTMLUListElement : HTMLOListElement;

type ListIntrinsicProps<TAs extends ListAs> = React.ComponentPropsWithoutRef<TAs>;

export type ListProps<Item, TAs extends ListAs = 'ul'> = Omit<
    // Use the intrinsic props of ul/ol, but prevent conflicts with our own props.
    ListIntrinsicProps<TAs>,
    'children'
> & {
    /** Sets the list type. @default "ul" */
    as?: TAs;
    /** The list of items to render */
    items: Item[];
    /** The component to render for each item */
    renderItem: (item: ListRenderItemInfo<Item>) => React.ReactElement;
    /** How to display the list. @default "vertical" */
    orientation?: 'horizontal' | 'vertical';
    /**
     * Sets gap between list items. @default false
     * Should be `true` if `ListItem` kind is `bordered`.
     */
    compact?: boolean;
    /** Sets a border and rounded corners on the list. @default false */
    bordered?: boolean;
    /** The element to render as a footer */
    footer?: React.ReactNode;
    /** Set to false to disable scrolling, defaults to true */
    scrollable?: boolean;
};

/**
 * Flat list of items.
 */
export const List = function List<Item, TAs extends ListAs = 'ul'>({
    ref,
    ...props
}: ListProps<Item, TAs> & { ref?: React.Ref<ListElement<TAs>> }): React.ReactElement {
    const {
        as = 'ul',
        items,
        renderItem,
        footer,
        orientation = 'vertical',
        compact = false,
        bordered = false,
        scrollable = true,
        className,
        ...rest
    } = props;

    const sharedClasses = `flex flex-1 size-full ${scrollable ? 'scrollbar' : ''}`;
    const orientationClasses = orientation === 'vertical' ? 'flex-col' : 'flex-row';
    const overflowClasses = scrollable
        ? orientation === 'vertical'
            ? 'overflow-y-auto scroll-gradient-mask-y'
            : 'overflow-x-auto scroll-gradient-mask-x'
        : '';
    const compactClasses = compact ? 'gap-0' : 'gap-1';
    const borderedClasses = bordered ? 'border-[0.5px] rounded-lg' : '';

    const ListElement = as;

    if (footer) {
        return (
            <div
                className={clsx(
                    sharedClasses,
                    orientationClasses,
                    compactClasses,
                    borderedClasses,
                    className
                )}
            >
                <ListElement
                    ref={ref as React.Ref<HTMLUListElement> & React.Ref<HTMLOListElement>}
                    className={clsx(
                        sharedClasses,
                        orientationClasses,
                        compactClasses,
                        overflowClasses
                    )}
                    {...(rest as React.ComponentPropsWithoutRef<TAs>)}
                >
                    {items.map((item, index) => renderItem({ item, index }))}
                </ListElement>

                {footer}
            </div>
        );
    }

    return (
        <ListElement
            ref={ref as React.Ref<HTMLUListElement> & React.Ref<HTMLOListElement>}
            className={clsx(
                sharedClasses,
                orientationClasses,
                overflowClasses,
                compactClasses,
                borderedClasses,
                className
            )}
            {...(rest as React.ComponentPropsWithoutRef<TAs>)}
        >
            {items.map((item, index) => renderItem({ item, index }))}
        </ListElement>
    );
} as <Item, TAs extends ListAs = 'ul'>(
    props: ListProps<Item, TAs> & React.RefAttributes<ListElement<TAs>>
) => React.ReactElement;
